import React, { useState, useEffect, useRef, useId, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import ApexCharts from "apexcharts";
import { Box, Typography } from '@mui/material';
import VerticalSlider from '../item/slider_vertical'
import Progress from '../item/progress'
import TooltipRiskScatter from './tooltip_risk_scatter';

export default function HeatmapRisk(props) {
  const [series, setSeries] = useState([]);
  const [sliderValue, setSliderValue] = useState([0, props.distance]);
  const [allData, setAllData] = useState([]);
  const [dateAry, setDateAry] = useState([]);
  const [isSlider, setIsSlider] = useState(false);
  const [dataLength, setDataLength] = useState(0);
  const [isNoData, setIsNoData] = useState(false);

  // チャートごとの一意のキー
  const chartId = useId();

  // 監視用Ref
  const sliderValueRef = useRef(sliderValue);

  // 監視用Ref（ホイール）
  const containerRef = useRef(null);
  const wheelHandlerRef = useRef(null);
  const wheelTimeoutRef = useRef(null);

  const handleWheel = (e) => {
    if (!isSlider || dataLength <= props.distance) return;
    e.preventDefault();
    if (wheelTimeoutRef.current) return;

    const delta = e.deltaY;
    const stepSize = Math.max(1, Math.round(Math.abs(delta) / 50)); // 感度: 50 を小さくすると更に速くなる
    setSliderValue(prev => {
      const maxStart = Math.max(0, dataLength - props.distance);
      let start = prev[0] + (delta > 0 ? -stepSize : stepSize);
      if (start < 0) start = 0;
      if (start > maxStart) start = maxStart;
      return [start, start + props.distance];
    });

    wheelTimeoutRef.current = setTimeout(() => {
      wheelTimeoutRef.current = null;
    }, 10); // スロットル(ms)。小さくすると連続で早く反応
  };

  const loadingEnd = () => {
    props.setReady(prev => {
      const updated = { ...prev };
      props.isReadyKey.forEach(key => {
        if (key in updated) {
          updated[key] = true;
        }
      });
      return updated;
    });
  }

  // データ更新が完了したらローディング終了
  useEffect(() => {
    if (allData !== null) {
      const timer = setTimeout(() => {
        loadingEnd();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [allData]);

  useEffect(() => {
    if (props.dateArray === null ||
      props.allRiskDatetime === null) {
      setIsNoData(true)
      loadingEnd()
      return
    }
    if (props.dateArray && props.dateArray?.length > 0
      && props.allRiskDatetime && props.allRiskDatetime?.length > 0) {
      setChartData(getHeatmapData(props.dateArray, props.allRiskDatetime), props.dateArray);
    }
    setIsNoData(false)
  }, [props.dateArray, props.allRiskDatetime]);

  useEffect(() => {
    if (props.dateArray === null ||
      props.allRiskDatetime === null) {
      setIsNoData(true)
      loadingEnd()
      return
    }
    if (props.dateArray && props.dateArray?.length > 0
      && props.allRiskDatetime && props.allRiskDatetime?.length > 0) {
      setChartData(getHeatmapData(props.dateArray, props.allRiskDatetime), props.dateArray);
    }
    return () => {
        ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
    };
  }, []);

  useEffect(() => {
    wheelHandlerRef.current = handleWheel;
  }, [handleWheel]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const fn = (e) => {
      if (wheelHandlerRef.current) wheelHandlerRef.current(e);
    };
    el.addEventListener('wheel', fn, { passive: false });
    return () => el.removeEventListener('wheel', fn);
  }, []);

  // ヒートマップ用の形式に変換
  const getHeatmapData = (date_ary, area_val_ary) => {
    const isOver = props.distance < area_val_ary?.length
    const chartData = props.getHeatmapData(date_ary, area_val_ary, isOver);
    if (isOver) {
      // スクロールONの場合は制限件数を切り取って返す
      return chartData.slice(props.limitDataCount * -1);
    }
    return chartData
  }

  // チャート情報を格納
  const setChartData = (chartData, date_ary) => {
    const isOver = props.distance < chartData?.length

    // sliderValueに応じてリスクの最大値を並べ替え
    let final_ary = []
    if (isOver) {
      // for (let i = sliderValue[1]; i > sliderValue[0] - 1; i--) {
      for (let i = sliderValueRef.current[1]; i > sliderValueRef.current[0] - 1; i--) {

        if (chartData?.length > i) {
          final_ary.unshift(chartData[i])
        }
      }
    } else {
      final_ary = chartData
    }

    setAllData(chartData)
    setSeries(final_ary)
    setIsSlider(isOver)
    setDateAry(date_ary)
    setDataLength(chartData?.length);
  }

  // スライダーが移動された場合
  useEffect(() => {
    sliderValueRef.current = sliderValue;
    if (props.isReadyFlg
        && allData.length > 0
    ) {
      setChartData(allData, dateAry)
    }
  }, [sliderValue, props.isReadyFlg]);

  const options = useMemo(() => ({
    chart: {
      type: 'heatmap',
      toolbar: {
        show: false
      },
      animations: {
        enabled: false
      },
      background: '#000',
      events: {
        dataPointSelection: (event, chartContext, config) => {
          props.setReady(prev => {
            const updated = Object.fromEntries(
              Object.keys(prev).map(key => [key, false])
            );
            return updated;
          });
          const seriesIndex = config.seriesIndex;
          const dataPointIndex = config.dataPointIndex;
          if (seriesIndex === -1 || dataPointIndex === -1) return;
          const clickedItem = series[seriesIndex]?.data[dataPointIndex];
          props.heatmapClickEvent(clickedItem)
        },
        dataPointMouseEnter: (event, chartContext, config) => {
          const { seriesIndex, dataPointIndex } = config;
          if (seriesIndex === -1 || dataPointIndex === -1) {
            props.setTooltipInfo(null);
            return;
          }
          const point = chartContext.w.config.series[seriesIndex].data[dataPointIndex];
          if (!point) return;
          props.setTooltipInfo({
            x: event.clientX,
            y: event.clientY,
            data: point,
            tooltipKey: props.tooltipKey
          });
        },
        mouseLeave: () => {
          props.setTooltipInfo(null);
        },
        mouseMove: (event, chartContext, config) => {
          props.setTooltipInfo(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              x: event.clientX,
              y: event.clientY,
            };
          });
        },
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0,
        radius: 0,
        useFillColorAsStroke: false,
        colorScale: props.colorScale(),
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      width: 1,
      colors: ['#333']
    },
    xaxis: {
      categories: dateAry,
      labels: {
        rotate: -45,
        rotateAlways: true,
        style: {
          colors: '#fff',
          fontSize: '11px'
        }
      },
      axisBorder: {
        show: true,
        color: '#444'
      },
      axisTicks: {
        show: true,
        color: '#444'
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: '#fff',
          fontSize: '12px'
        },
        formatter: (value) => {
          const str = String(value);
          return '　'.repeat(Math.max(0, 5 - str.length)) + str;
        }
      }
    },
    grid: {
      borderColor: '#333',
      padding: {
        top: 0,
        right: 20,
        bottom: 0,
        left: 10
      }
    },
    legend: {
      show: props.isShowLegend ?? true,
      position: 'right',
      horizontalAlign: 'center',
      floating: false,
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      labels: {
        colors: '#fff'
      },
      markers: {
        width: 20,
        height: 20,
        radius: 2
      },
      itemMargin: {
        horizontal: 0,
        vertical: 8
      }
    },
    tooltip: {
      enabled: false,
    },
    annotations: {
      position: 'front',
      xaxis: [
        {
          x: dateAry[12],
          borderColor: '#0000FF',
          borderWidth: 1,
          strokeDashArray: 4,
          label: {
            show: false,
          }
        }
      ]
    }
  }), [dateAry, series, props.isShowLegend]);

  // チャートのデータが更新されたら強制再描画
  useEffect(() => {
    if (series && series.length > 0) {
      ApexCharts.exec(chartId,
        "updateSeries", series);
    }
  }, [series]);

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          height: '100%',
          overflow: 'hidden',
          backgroundColor: '#000'

        }}
      >
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {!props.isReadyFlg ?
            <Progress />
            :
            isNoData ?
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                対象データがありません
              </Typography>
              : <>
                <ReactApexChart
                  options={options}
                  series={series}
                  type="heatmap"
                  height="100%"
                  width="100%"
                  style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
                />
                {props?.tooltipInfo && props?.tooltipInfo?.tooltipKey === props?.tooltipKey && (
                  <Box
                    sx={{
                      position: 'fixed',
                      left: Math.min(window.innerWidth - 160, props.tooltipInfo.x),
                      top: Math.min(window.innerHeight - 100, props.tooltipInfo.y) + 10,
                      transform: 'translateX(-50%)',
                      maxWidth: 300,
                      whiteSpace: props?.whiteSpace || 'normal',
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      color: '#000',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      pointerEvents: 'none',
                      boxShadow: '0 2px 8px rg?ba(0,0,0,0.15)',
                      zIndex: 9999,
                      transition: 'all 0.15s ease-out',
                      opacity: props?.tooltipInfo ? 1 : 0,
                    }}
                  >
                    <TooltipRiskScatter
                      tooltipInfo={props?.tooltipInfo}
                      tooltipKey={props?.tooltipInfo?.tooltipKey}
                      renderBody={props?.renderBody}
                    />
                  </Box>
                )}
              </>
          }

        </Box>
        {isSlider &&
          <Box
            sx={{
              width: '20px',
              marginLeft: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <VerticalSlider
              dataLength={dataLength}
              value={sliderValue}
              setValue={setSliderValue}
              distance={props.distance}
              height={'90%'}
            />
          </Box>
        }
      </Box>
    </>
  );
};